import {
    useRef,
    useMemo,
    useState,
    useEffect,
    useContext,
    useCallback,
} from "react";
import {
    Callout,
    Spinner,
    IconButton,
    SpinnerSize,
    ThemeProvider,
    DirectionalHint,
} from "@fluentui/react";

import { pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";

import { toast } from "react-toastify";
import _, { set } from "lodash";

import styles from "./XplrrPDF.module.css";
import customTheme from "./../../theme/customTheme";

import { FaWindowClose } from "react-icons/fa";
import { HiMiniPlayPause } from "react-icons/hi2";
import { HiOutlineVolumeUp, HiOutlineVolumeOff } from "react-icons/hi";

import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import smoothScrollIntoView from "smooth-scroll-into-view-if-needed";

import { GlobalContext } from "../../GlobalContext";
import {
    gfile,
    gcontentlist,
    gloggedinuser,
    highLightItem,
    gXplrrFileOrder,
    gXplrrSpaceOrder,
    gXplrrSpaceIndex,
    gXplrrExtendedFileList,
    gXplrrContentListFileOrder,
    empty_extraction_field_list,
    gXplrrCitationChangeCallback
} from "../../interfaces";
import { prepareHighlightRegexInvIncluded } from "../../rgx_glob";
import {
    wigglePixelV2,
    addDotIfNeeded,
    init_inv_globs,
    additionalFixes,
    stopWigglePixel,
    getNewWidthActual,
    filterHighlightString,
    getHeaderGlob
} from "../../util_glob";
import {
    pb_excl,
    ipaDocTypes,
    assistantCodes,
    pdfSourceCodes,
    emptyValNumObj,
    pdfsrccodestype,
    resizerBarsWidth,
    applyReplacements,
    xplrrContentListFileOrderKey
} from "../../lsts";
import {
    playButStyles,
    closeButStyles,
    pauseButStyles
} from "../../styles_glob";
import {
    scrollToPageCSTM,
    scrollToPageDefault,
    scrollToPageNoSmooth,
    scrollCustomAnimPage,
    scrollCustomAnimElement,
    reendableUserScrollHandling
} from "../../scrll_glob";

import {
    fetchSpeechAOI,
    getClearTextForAudio,
    getVoiceVersion,
    prepSsmlDocReader
} from "../../api/aud";

import { qReadify, qJsonAnalysisRecall, qUpdateAiContent } from "../../api";
import { qResolveParagraphHighlight } from "../../api/api";
import { scrollAndHighlightParagraphs, clearCitationHighlights } from "../ParagraphOverlayFX/paragraphHighlightUtil";
import { qUpdateInvoiceStructuredData, qApprove } from "../../api/qpi";

import XplrrBOXPDF from "../XplrrBOXPDF/XplrrBOXPDF";
import JsonGridGen from "../../components/JsonGridGen/JsonGridGen";
import JsonViewerInteractive from "../../components/JsonGridGen/JsonViewerInteractive";
import PageSelector from "../PageSelector/PageSelector";
import FileDropdown from "../FileDropdown/FileDropdown";
import XplrrBOXPDFZoom from "../XplrrBOXPDF/XplrrBOXPDFZoom";
import JsonGridInvoice from "../JsonGridInvoice/JsonGridInvoice";
import InvoiceItemsTable from "../JsonGridInvoice/InvoiceItemsTable";
import { renderLinkData } from "./renderLinkData";
import { getDefaultFileOrderFromCache, fileMapGen } from "./XplrrPDFUtil";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface ViewerPDFProps {
    startPage: string;
    citationFileId: string;
    xplrrCitationChangeCallback: (callBackProps: gXplrrCitationChangeCallback) => void;
    fileId: string;
    refPageNum: string;
    isCitation: boolean;
    rndo?: string;
    searchString: string;
    activeGroupState: string;
    fileList: gfile[];
    shouldPlayAudio: string;
    isIframe: boolean;
    onXplrrPDFDismiss: () => void;
    onDataHighlight: (searchString: string) => void;
    isLib: string;
    loggedInUser: gloggedinuser;
    onInvValueUpdate: (fileids?: string, isfromchat?: boolean, gid?: string, data_tables_change?: boolean) => void;
    invDataOverride?: any;          // 🕰️ transient invoicedata (e.g. a previous version) — view-only, never saved/cached
}

const XplrrPDF = ({
    startPage,
    citationFileId,
    xplrrCitationChangeCallback,
    fileId,
    refPageNum,
    isCitation,
    rndo,
    searchString,
    activeGroupState,
    fileList,
    shouldPlayAudio,
    isIframe,
    onXplrrPDFDismiss,
    onDataHighlight,
    isLib,
    loggedInUser,
    onInvValueUpdate,
    invDataOverride
}: ViewerPDFProps) => {
    const { sourceHighlighting, docAudio, selectedGroup } = useContext(GlobalContext);
    const [file, setFile] = useState<gfile>();                                                  // current file
    const [flId, setFlId] = useState<string>();                                                 // current file id
    const [fileMap, setFileMap] = useState<gXplrrSpaceOrder>({});                               // full file ⇒ item map
    const [numPages, setNumPages] = useState<number>();                                         // number of doc pages
    const [invUpdate, setInvUpdate] = useState<any>();
    const [audioPage, setAudioPage] = useState<number>(0);                                      // audio page currently spoken
    const [pageLoaded, setPageLoaded] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [fileInvData, setFileInvData] = useState<any>();                                      // current file structured data ⇒ dpa, inv
    const [docRendered, setDocRendered] = useState<boolean>(false);
    const [activeFileId, setActiveFileId] = useState<string>();
    const [manualSection, setManualSecton] = useState<number>();
    const [referencePage, setReferencePage] = useState<number>(0);
    const [fileListStateVar, setFileListStateVar] = useState<gfile[]>([]);                      // files to showcase
    const [contentFileOrder, setContentFileOrder] = useState<gXplrrContentListFileOrder>({});
    const [activeCitationState, setActiveCitationState] = useState<string>();
    const [transitTotalRowArrMap, setTransitTotalRowArrMap] = useState<Map<string, any[]>>(new Map());
    const [isContentTableShowing, setIsContentTableShowing] = useState<boolean>(false);
    const [resizeBackTriggerCount, setResizeBackTriggerCount] = useState<number>(0);
    const [isStructureSideBarOpen, setIsStructureSideBarOpen] = useState<boolean>(false);
    const [perFilesPerSectionStateMap, setPerFilesPerSectionStateMap] = useState<Map<string, Map<string, boolean>>>(new Map());
    const [contentRefreshTriggerIncrement, setContentRefreshTriggerIncrement] = useState(0);
    const [srcId, setSrcId] = useState<pdfsrccodestype>(
        (selectedGroup.invoicesgroup == 1
            && selectedGroup.assistanttype != assistantCodes.dpa && file?.doc_category != 'data' )
            || (selectedGroup.assistanttype == assistantCodes.dpa && isStructureSideBarOpen == true)
            ? pdfSourceCodes.inv
            : pdfSourceCodes.modal
    );
    // audio state
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const audioRef = useRef<sdk.SpeakerAudioDestination | null>(null);
    const synthesizerRef = useRef<sdk.SpeechSynthesizer | null>(null);
    const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

    // AI overlay state
    const [isAiOverlayOpen, setIsAiOverlayOpen] = useState(false);
    const [aiOverlayData, setAiOverlayData] = useState<any>(null);
    const [aiOverlayFieldorder, setAiOverlayFieldorder] = useState<string[]>([]);
    const [aiOverlayHeight, setAiOverlayHeight] = useState(45); // percentage of vh
    const [isAiDragging, setIsAiDragging] = useState(false);
    const showAiOverlay = selectedGroup?.contenttype_config?.postaianalysisoptions?.preconfopt == "4" && !!file?.aiinteractions?.length;

    const handleAiDragStart = useCallback((e: React.MouseEvent) => {
        //** ai overlay drag handlers */
        e.preventDefault();
        setIsAiDragging(true);
    }, []);

    useEffect(() => {
        if (!isAiDragging) return;
        const handleMouseMove = (e: MouseEvent) => {
            const newHeight = ((window.innerHeight - e.clientY) / window.innerHeight) * 100;
            setAiOverlayHeight(Math.min(90, Math.max(15, newHeight)));
        };
        const handleMouseUp = () => setIsAiDragging(false);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isAiDragging]);

    // runs from outside [citations]
    useEffect(() => {
        if (activeGroupState != selectedGroup.selectionId) return;  // prevent runs before the space has aligned
        if (fileList.length == 0) return;

        setActiveCitationStateFunc(citationFileId);

        if (fileId != flId || fileId != activeFileId) {
            window.globalBase64String = "";
            if (window.globalPDFPageDimensions == undefined) window.globalPDFPageDimensions = [];

            setFlId(fileId);
            setActiveCitationState(citationFileId);

            const fileToLoad = fileList.find(f => f.fileid == fileId);
            setActiveFileId(fileId);
            setFile(fileToLoad);
            setCurrentPageFunc(parseInt(startPage));
            setReferencePage(parseInt(startPage));
        } else {
            if (selectedGroup.assistanttype != assistantCodes.ipa) setReferencePage(parseInt(startPage));
            setHighlightsPerfAfterLoad();
        }
    }, [rndo]);

    // activefileid
    useEffect(() => {
        if (activeGroupState != selectedGroup.selectionId) return;
        if (fileList.length == 0) return;

        if (activeCitationState && fileId == flId && activeFileId == flId) setHighlightsMain();
        const fileMap: gXplrrSpaceOrder = fileMapGen(fileList);                                     // generate content list file order and item opened map
        const cachedFileOrder: gXplrrContentListFileOrder = setContentFileOrderToState(fileList);   // order items, based on local state cache or default
        const extendedFileList: gXplrrExtendedFileList[] = fileList.map((flfile, flIndex) => {
            return {
                ...flfile,
                globalXplrrOrder: cachedFileOrder[flfile.fileid] == null ? fileMap[parseInt(flfile.fileid)].order : cachedFileOrder[flfile.fileid]
            };
        });
        setFileMap(fileMap); // preprocess - need data for dynamic content list
        setFileListStateVar(extendedFileList.sort((a, b) => a.globalXplrrOrder - b.globalXplrrOrder));
    }, [activeFileId]);

    // contentfileorder
    useEffect(() => {
        if (activeGroupState != selectedGroup.selectionId) return;
        if (fileList.length == 0) return;

        // preserve file order to local cache
        if (Object.keys(contentFileOrder).length > 0) {
            const fileOrderCache = localStorage.getItem(xplrrContentListFileOrderKey);
            if (fileOrderCache) {
                const orderCacheParsed: gXplrrSpaceIndex = JSON.parse(fileOrderCache);
                orderCacheParsed[selectedGroup.selectionId] = contentFileOrder;
                localStorage.setItem(xplrrContentListFileOrderKey, JSON.stringify(orderCacheParsed));
            } else {
                const orderCache: gXplrrSpaceIndex = {};
                orderCache[selectedGroup.selectionId] = contentFileOrder;
                localStorage.setItem(xplrrContentListFileOrderKey, JSON.stringify(orderCache));
            }
        }
    }, [contentFileOrder]);

    // 🕰️ true when viewing a transient version (override provided) — save/edit disabled
    const isVersionView = useMemo(() => invDataOverride != null, [invDataOverride]);

    // filelist
    useEffect(() => {
        if (fileId) {
            const sel_file = fileList.find(f => f.fileid == fileId);
            // 🕰️ when a version override is supplied, show it (cloned) instead of the live list data.
            // the override is never written back to fileList / cache — purely for viewing.
            setFileInvData(structuredClone(invDataOverride ?? sel_file?.invoicedata ?? {})); // to guarantee original object is not mutated and preserved accross closes
            //setFileInvData(sel_file?.invoicedata || {});
            if (selectedGroup.invoicesgroup == 1) setFile(sel_file);
        }
    }, [fileList, fileId, invDataOverride]);

    // ai overlay data fetch
    useEffect(() => {
        if (!showAiOverlay || !file?.aiinteractions?.[0]) return;
        qJsonAnalysisRecall(file.aiinteractions[0], file?.doc_category, getHeaderGlob(loggedInUser), selectedGroup.selectionId, file?.fileid).then(res => {
            if (res?.content) {
                setAiOverlayData(JSON.parse(res.content)?.result);
                setAiOverlayFieldorder(res.fieldorder ? JSON.parse(res.fieldorder) : []);
            }
        });
    }, [file?.aiinteractions?.[0], showAiOverlay]);

    // selected assistant
    useEffect(() => {
        setSrcId(
            (selectedGroup.invoicesgroup == 1
                && selectedGroup.assistanttype != assistantCodes.dpa && file?.doc_category != 'data')
            || (selectedGroup.assistanttype == assistantCodes.dpa
                && isStructureSideBarOpen == true)
                ? pdfSourceCodes.inv
                : pdfSourceCodes.modal
            );
    }, [selectedGroup, file]);

    // shouldplayaudio
    useEffect(() => {
        stopSpeech();
    }, [shouldPlayAudio]);

    // perfilespersectionstatemap
    useEffect(() => {
        setContentRefreshTriggerIncrement(prevValue => prevValue + 1);
    }, [perFilesPerSectionStateMap, fileId]);

    // pdf page numbers - set page total
    const setPages = (numP: number): void => {
        setNumPages(numP);
        init_inv_globs(); // init window vars
    };

    // pdf page numbers - handle current page number state
    const handleScroll = (pageNumCur: number) => {
        if (pageNumCur != currentPage) setCurrentPageFunc(pageNumCur);
    };

    // set active citation
    const setActiveCitationStateFunc = (citI: string) => {
        setActiveCitationState(citI);
    };

    // set active file id & wipe inv update state
    const setActiveFileIdFunction = (flId: string) => {
        if (activeFileId != flId){
            setInvUpdate(undefined);
            setTransitTotalRowArrMap(new Map());
            setActiveFileId(flId);
        }
    };

    // save invoice items - merge between main inv items and line items
    const recieveInvItemsMergeAndSave = (gridData: any, fid: string, approve?: boolean) => {
        // 🕰️ note: saving while viewing a previous version is allowed — it restores/edits that
        // version's data as the new current invoicedata.
        let safeInvUpdate = {}
        if (!invUpdate) {
            safeInvUpdate = gridData;
            setInvUpdate(gridData);
        } else {
            // invUpdate holds the main-fields grid (loaded once); gridData is the line-items grid, which is
            // the one PO editing updates. Carry invoice_items AND the PO-edit-affected fields
            // (purchase_order_info + top-level purchase_order) from gridData so a reassignment isn't
            // overwritten by invUpdate's stale copy on save.
            const poMerge = {
                invoice_items: gridData.invoice_items,
                ...('purchase_order_info' in gridData ? { purchase_order_info: gridData.purchase_order_info } : {}),
                ...('purchase_order' in gridData ? { purchase_order: gridData.purchase_order } : {}),
            };
            safeInvUpdate = { ...invUpdate, ...poMerge };
            setInvUpdate((prev: any) => ({ ...prev, ...poMerge }));
        }
        if (loggedInUser) {
            qUpdateInvoiceStructuredData(loggedInUser, safeInvUpdate, fid, selectedGroup.selectionId, selectedGroup.extractionfieldlist?.allowed_deviation || 1).then(res => {
                if (res.status == "success") {
                    toast.success("Invoice data updated successfully!");
                    if (approve) {
                        qApprove(loggedInUser, fid, selectedGroup.selectionId).then(res => {
                            onInvValueUpdate(undefined, undefined, undefined, true);
                        });
                    } else {
                        onInvValueUpdate(undefined, undefined, undefined, true);
                    }
                } else {
                    toast.error("Error updating invoice data");
                }
            });
        }
    };

    // inv item cell updated by user
    const invItemsCellUpdCallback = (invItems: any[], fid: string | number, totalRowArr: any[]) => {
        setTransitTotalRowArrMap(prev => {
            const newMap = new Map(prev);
            newMap.set(fid?.toString() ?? "0", totalRowArr);
            return newMap;
        });
    }

    // highlights - set highlights after load (triggered by rndo useeffect)
    const setHighlightsPerfAfterLoad = useCallback(async () => {
        if (!searchString) { // nav req without highlights
            if (selectedGroup.assistanttype != assistantCodes.ipa) {
                setCurrentPageFunc(parseInt(startPage));
                scrollCustomAnimPage(parseInt(startPage), currentPage);
            }
            import.meta.env.DEV && console.log("‹hil_no_str›");
            return;
        }

        removeFontColorToMatchesRed();
        if (activeCitationState && sourceHighlighting == true) applyHighlightsPerf();
        const allResults = await applyHighlightMatches();
        const allResultsNull = allResults?.every(result => result === null);
        if (allResultsNull) {
            if (startPage) {
                if (selectedGroup.assistanttype != assistantCodes.ipa) scrollToPageNoSmooth(parseInt(startPage), '‹shpal›');
                reendableUserScrollHandling('‹shpal›');
            }
        }
        setPageLoaded(true);
        import.meta.env.DEV && console.log("‹hil›", {searchString});
    }, [searchString, startPage, activeCitationState, sourceHighlighting]);

    // highlights - set highlights on activefile change and on load time callback from pdfbox
    const setHighlightsMain = async () => {
        if (!searchString) { // nav req without highlights
            if (selectedGroup.assistanttype != assistantCodes.ipa) {
                setCurrentPageFunc(parseInt(startPage));
                scrollCustomAnimPage(parseInt(startPage), currentPage);
            }
            import.meta.env.DEV && console.log("‹h_main_no_str›");
            return;
        }
        if (activeCitationState && isCitation && sourceHighlighting == true) applyHighlightsPerf();
        removeFontColorToMatchesRed();
        const allResults     = await applyHighlightMatches();
        const allResultsNull = allResults?.every(result => result === null);
        if (selectedGroup.assistanttype != assistantCodes.ipa) if (allResultsNull && startPage) scrollToPageNoSmooth(parseInt(startPage), '‹hl_main›');

        reendableUserScrollHandling('‹hl_main›', 100);
        stopWigglePixel("‹wiggle_pixe1›");
        import.meta.env.DEV && console.log("‹h_main›", {searchString});
    };

    // highlights -apply highlights
    const applyHighlightsPerf = () => {
        if (file?.indexchunks) {
            stopWigglePixel("‹wigglepixe2›");
            const highlightPages: number[] = findRange([...file.indexchunks, file.pagedimensions.length], parseInt(startPage));
            for (let i = 0; i < highlightPages.length; i++) {
                document.querySelectorAll(`div[data-page-number="${highlightPages[i]}"] span`).forEach(span => {
                    (span as HTMLElement).classList.add(styles.citHglghtr);
                });
            }
        }
    };

    // highlights - find highlights - from setHighlightsV3  or after from applyHighlightMatches
    const findMatchingSequenceRGX = async (pg: number[], pageCountDyn: number) => {
        const filteredTargetString = filterHighlightString(searchString);
        let rgx = prepareHighlightRegexInvIncluded(filteredTargetString, selectedGroup.assistanttype);

        const selectorPG = pg.map(pg => `.pdf_itm_${pg - 1}`).join(", ");
        const items = document.querySelectorAll(selectorPG);
        const itemsArray = Array.from(items);

        let processedText = '';
        let elementMap: highLightItem[] = [];

        for (let index = 0; index < itemsArray.length; index++) {
            const item = itemsArray[index] as HTMLElement;
            const originalItemText = item.textContent?.trim();

            const itemText = filterHighlightString(originalItemText);
            if (itemText.length > 0) {
                elementMap.push({
                    startIndex: processedText.length,
                    endIndex: processedText.length + itemText.length - 1,
                    element: item as HTMLElement,
                    processedText: itemText
                });
                processedText += itemText;
            }
        }

        let matches = [...processedText.matchAll(rgx)];
        if (selectedGroup.assistanttype == assistantCodes.ipa) {
            matches = matches.filter(
                match =>
                    (match && match.length < 2 && match[0].length <= searchString.length) ||
                    (match && match.length > 1 && match[1].length <= searchString.length)
            );
        }
        matches.forEach(match => {
            let startIndex = match.index || 0;
            const endIndex = startIndex + match[0].length - 1;

            const matchingElements = elementMap.filter(
                item =>
                    (item.startIndex <= startIndex && startIndex <= item.endIndex) ||
                    (item.startIndex <= endIndex && endIndex <= item.endIndex) ||
                    (startIndex <= item.startIndex && item.endIndex <= endIndex)
            );
            matchingElements.forEach((item, index) => {
                if (index == matchingElements.length - 1) {
                    if (window.globalHiglightFound == true) {
                        import.meta.env.DEV === true && console.log("‹glbalhglghtfnd_not_true›", window.globalHiglightFound);
                    } else {
                        window.globalHiglightFound = true;
                        window.globalWigglePixel = 0;
                        try {
                            let itmPg = parseInt(item.element.id.split("_")[1]);
                            let skndEl = item.element;
                            if (index > 0 && skndEl.innerText.length < 5) skndEl = matchingElements[index - 1].element;
                            import.meta.env.DEV === true && console.log("‹numPages›", numPages, '‹pagedyn›', pageCountDyn);
                            if (pageCountDyn && pageCountDyn > 1) scrollCustomAnimElement(itmPg, skndEl, skndEl, currentPage);
                        } catch {
                            import.meta.env.DEV === true && console.log("‹scroll_failed›");
                            smoothScrollIntoView(item.element as HTMLElement, { behavior: "smooth", block: "center" });
                        }
                    }
                }
                item.element.classList.add(styles.citHglghtrRed);
            });
        });

        if (matches.length > 0 && matches[0].index !== undefined) {
            return { startIndex: matches[0].index, endIndex: matches[0].index + matches[0][0].length - 1 };
        } else {
            return null;
        }
    };

    // highlights - main workhorse for applying highlights
    const applyHighlightMatches = async () => {
        if (file?.indexchunks) {
            let highlightPages: number[] = findRange([...file.indexchunks, file.pagedimensions.length], parseInt(startPage));

            if (highlightPages.length > 0 && highlightPages[0] > 1) highlightPages = [highlightPages[0] - 1].concat(highlightPages); // add one more pages infront. need to deep dive in index/number logic

            window.globalHiglightFound = false;
            if (selectedGroup.assistanttype != assistantCodes.ipa) wigglePixelV2(highlightPages[0], currentPage);
            let pageCountDyn = file?.pagedimensions?.length ?? (numPages ?? 0);
            const promises = highlightPages.map((page, index) => setHighlightsV3(page, index, highlightPages.length, pageCountDyn));
            const results = await Promise.all(promises);
            const allResultsNull = results?.every(result => result === null);

            if (allResultsNull) {
                const results = await findMatchingSequenceRGX(highlightPages, pageCountDyn);
                return [results];
            } else {
                return results;
            }
        }
    };

    // highlights - wait for pdf pages to load to start highlight apply
    const waitForAllPDFItems = async (index: number, maxAttempts: number = 50): Promise<true | null> => {
        const isReactPdfPageTextRendererDone = (index: number): boolean => {
            let itemProgress = window.globalItemCounts?.get(index);
            if (itemProgress !== undefined && itemProgress.flag == true) {
                let pdfPageItemCount = document.querySelectorAll(`.pdf_itm_${index}`);
                if (pdfPageItemCount.length == itemProgress.count) {
                    return true;
                } else {
                    setReferencePage(index + 1);
                    return false;
                }
            } else {
                return false;
            }
        };
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (isReactPdfPageTextRendererDone(index)) return true;
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        console.log(`‹failed_render_wait› ⇒ ${index} after ${maxAttempts} attempts`);
        return null;
    };

    // highlights - apply highlights logic from applyHighlightMatches
    const setHighlightsV3 = async (page: number, i: number, lignth: number, pageCountDyn: number) => {
        await waitForAllPDFItems(page - 1, 50);
        if (window.globalHiglightFound == false) {
            if (i == lignth - 1) {
                const difBetweenSrcAndTrgPage = currentPage - page;
                if (Math.abs(difBetweenSrcAndTrgPage) > 5) {
                    if (difBetweenSrcAndTrgPage < 0) {
                        scrollToPageCSTM(currentPage + 1);
                    } else {
                        scrollToPageCSTM(currentPage - 1);
                    }
                }
            }
        }
        const result = await findMatchingSequenceRGX([page], pageCountDyn);
        return result;
    };

    // highlights - find page range for highlights
    const findRange = (numbers: number[], target: number): number[] => {
        numbers.sort((a: number, b: number) => a - b);

        let startIndex = numbers.findIndex((num: number) => num > target);

        let upper = startIndex === -1 ? numbers[numbers.length - 1] : numbers[startIndex];
        if (selectedGroup.assistanttype == assistantCodes.ipa && startIndex > 0) upper = numbers[numbers.length - 1]

        let endIndex = numbers.lastIndexOf(target);
        let lower = endIndex !== -1 ? numbers[endIndex] : numbers[startIndex - 1];

        if (lower === undefined || upper === undefined) return []; // return an empty array if target is out of the provided range

        let result = [];
        for (let i = lower; i <= upper; i++) result.push(i);

        if (target === upper) result.push(upper);

        try {
            window.globalpdfObservableRange = result.length;
        } catch {
            // do nothing
        }

        return result;
    };

    // highlights - remove highlights
    const removeFontColorToMatchesRed = (): void => {
        const elements = document.getElementsByClassName(styles.citHglghtrRed);
        while (elements.length > 0) elements[0].classList.remove(styles.citHglghtrRed);
    };

    // content table related
    const setContentFileOrderToState = (fileList: gfile[]) => {
        const fileOrderCache = localStorage.getItem(xplrrContentListFileOrderKey);
        if (fileOrderCache) {
            const fullFileOrderCacheParsed: gXplrrSpaceIndex = JSON.parse(fileOrderCache);
            const fileOrderCacheParsed: gXplrrContentListFileOrder = fullFileOrderCacheParsed[selectedGroup.selectionId];
            if (fileOrderCacheParsed && Object.keys(fileOrderCacheParsed)?.length > 0) {
                setContentFileOrder(fileOrderCacheParsed);
                return fileOrderCacheParsed;
            } else {
                const defaultOrder = getDefaultFileOrderFromCache(fileList);
                setContentFileOrder(defaultOrder);
                return defaultOrder;
            }
        } else {
            const defaultOrder = getDefaultFileOrderFromCache(fileList);
            setContentFileOrder(defaultOrder);
            return defaultOrder;
        }
    };

    // content list - handle click on content list dropdown
    const handleClick = (target: EventTarget, item: gcontentlist, indx: number) => {
        scrollToPageNoSmooth(item.destPageNum, '‹hndl_cl›');
        setCurrentPageFunc(item.destPageNum);
        reendableUserScrollHandling('‹hndl_cl');
    };

    // content list - handle click on file dropdown
    const handleFileClick = (target: EventTarget, fl: gfile, flIndex: number) => {
        setInvUpdate(undefined);
        setTransitTotalRowArrMap(new Map());
        setActiveFileId(flId);
        setFile(undefined);
        setFlId(undefined);
        setCurrentPageFunc(1);
        scrollToPageNoSmooth(1, '‹hndl_fl›');
        window.globalUserScrollLazyLoadEnabled == true;

        xplrrCitationChangeCallback({
            fileid: fl.fileid,
            filename: fl.name,
            destPageNum: "1",
            isCitation: false,
            rndo: Math.random().toString(36).substring(2, 6)
        });

        reendableUserScrollHandling('‹fl_clk›', 100);
    };

    // content list - content list expand collapse file dropdown
    const expandCollapseSection = (flId: string, srcText: string) => {
        setPerFilesPerSectionStateMap(prevMap => {
            const newMap     = new Map(prevMap);
            const fileMap    = newMap.get(flId) || new Map();
            const newFileMap = new Map(fileMap);
            newFileMap.set(srcText, !fileMap.get(srcText));
            newMap.set(flId, newFileMap);
            return newMap;
        });
    };

    // non-functional: content list - callback on content list - legacy
    const setContentList = (contentList: { title: string; dest: number }[]) => {
        import.meta.env.DEV === true && console.log("‹set_content_list›", contentList);
    };

    // content list - dropdown - oncontentshow callback
    const onContentShow = (fl: gfile | undefined, flIndex: number | undefined) => {
        setIsContentTableShowing(true);
    };

    // callback when pdf load ready
    const handleTimeout = () => {
        reendableUserScrollHandling('‹hndl_tout›');
        setDocRendered(true);
        if (fileId == activeFileId) setHighlightsMain();
        setPageLoaded(true);
    };

    // handle page change  from page selector
    const handlePageChangeFromSelector = (event: React.ChangeEvent<HTMLSelectElement>) => {
        window.globalUserScrollLazyLoadEnabled == true;

        setManualSecton(Number(event.target.value));
        setCurrentPageFunc(Number(event.target.value));
        scrollToPageNoSmooth(Number(event.target.value), '‹hndl_pg_chg›');
        reendableUserScrollHandling('‹hndl_pg_chg›');
    };

    // audio - play audio button clicked
    const playSpeech = () => {
        if (audioRef.current && audioPage == currentPage) {
            audioRef.current.resume();
            setIsPlaying(true);
        } else {
            audioRef.current = null;
            setIsLoadingAudio(true);
            fetchSpeechAudio(currentPage);
        }
    };

    // audio - stop audio button clicked
    const stopSpeech = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    // audio - handle audio ai
    const handleAudioAOI = async (page: number, retryCount = 0) => {
        setCurrentPageFunc(page);
        let text = getClearTextForAudio(page);
        if (text && text.trim().length > 0) {
            let aud = await fetchSpeechAOI(text);
            if (aud) {
                aud.loop = false;
                aud.onended = () => {
                    if (numPages && page + 1 < numPages) {
                        scrollToPageDefault(page + 1);
                        handleAudioAOI(page + 1);
                    }
                };
                setAudio(aud);
                aud.play();
                setIsLoadingAudio(false);
                setIsPlaying(true);
            }
        } else {
            if (numPages && currentPage < numPages) {
                scrollToPageDefault(page + 1);
                handleAudioAOI(page + 1);
            }
        }
    };

    // audio - advance current page
    const setCurrentPageFunc = (page: number) => {
        setCurrentPage(page);
        window.globalCurrentPage = page;
    };

    // audio - get speech audio
    const fetchSpeechAudio = async (page: number, retryCount = 0) => {
        const r = import.meta.env.VITE_STT_R;
        const a = import.meta.env.VITE_STT_K;

        setCurrentPageFunc(page);
        setAudioPage(page);

        const items = document.querySelectorAll(`.pdf_itm_${page - 1}`);
        let text = Array.from(items)
            .filter(
                item =>
                    item.textContent != null &&                                 // exclude if string is empty
                    !pb_excl.includes(item.textContent.toLowerCase().trim()) && // exclude if string is in the exclusion array
                    item.textContent.trim() !== "|"
            )
            .map(item => item.textContent)
            .join(" ");

        let readify = await qReadify(text, selectedGroup);

        text = applyReplacements(readify);
        text = additionalFixes(text);

        if (text && text.trim().length > 0) {
            try {
                let player = new sdk.SpeakerAudioDestination();
                player.onAudioEnd = function (_) {
                    if (numPages && page + 1 < numPages) {
                        scrollToPageDefault(page + 1);
                        player.close();
                        fetchSpeechAudio(page + 1);
                    }
                };
                player.onAudioStart = function (_) {
                    import.meta.env.DEV === true && console.log("‹aud_start_xplrr_pdf›");
                };

                let voiceVer = getVoiceVersion(text, selectedGroup.convolang);
                let voiceP   = voiceVer.voice;
                let lcle     = voiceVer.language;

                audioRef.current    = player;
                let audioConfig     = sdk.AudioConfig.fromSpeakerOutput(player); // type: sdk.AudioConfig;
                const speechConfig  = sdk.SpeechConfig.fromSubscription(a, r);
                speechConfig.speechSynthesisVoiceName = voiceP;

                const synthesizer   = new sdk.SpeechSynthesizer(speechConfig, audioConfig);
                synthesizer.synthesisCompleted = (sender, event) => {
                    import.meta.env.DEV === true && console.log("synth_cmplt_xplrrr_pdf");
                };

                synthesizerRef.current = synthesizer;
                setIsPlaying(true);
                synthesizer.speakSsmlAsync(
                    prepSsmlDocReader(text, voiceP, lcle),
                    result => {
                        if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
                            setIsLoadingAudio(false);
                            synthesizer.close();
                        }
                    },
                    error => {
                        console.error("errr_synth", error);
                        setIsPlaying(false);
                        synthesizer.close();
                    }
                );
            } catch (error: any) {
                console.error("‹errr_fetch_speech›", error);
            }
        } else {
            if (numPages && currentPage < numPages) {
                scrollToPageDefault(page + 1);
                fetchSpeechAudio(page + 1);
            }
        }
    };




    // non-functional: audio - is loading
    const callBackIsLoading = (isLoading: boolean) => {
        import.meta.env.DEV === true && console.log("‹callback_loading_page›", currentPage, "‹file›", file);
    };

    // non-functional: json grid invoice - refresh data callback
    const invRefreshRequestMngCenter = () => {
        // setTableEditTriggerCount(prevCount => prevCount + 1);
    };

    // pdfbox - visible entries callback
    const callBackVisibleEntries = (visibleEntries: number[]) => {};

    // resizable divider state and handlers
    const [panelWidth, setPanelWidth] = useState<number>(selectedGroup.assistanttype == assistantCodes.dpa ? 0 : 0);
    const [isResizing, setIsResizing] = useState<boolean>(false);
    const [panelHeight, setPanelHeight] = useState<number>(300);
    const [panelWidthRight, setPanelWidthRight] = useState<number>(window.innerWidth - (panelWidth + resizerBarsWidth));
    const [isVerticalResizing, setIsVerticalResizing] = useState<boolean>(false);

    const eclientx = useRef<number>(0);
    const eclienty = useRef<number>(0);
    const topResizing = useRef<number>(0);
    const leftResizing = useRef<number>(0);
    const wasresizingref = useRef<boolean>(false);
    const dividerRef = useRef<HTMLDivElement>(null);
    const leftDividedRef = useRef<HTMLDivElement>(null);
    const rightDividedRef = useRef<HTMLDivElement>(null);
    const verticalDividerRef = useRef<HTMLDivElement>(null);
    const wasverticalresizingref = useRef<boolean>(false);

    const sePanelWidthIfDiff = (newWidth: number, pW:number) => {
        // console.log("‹sePanelWidthIfDiff›", {newWidth, panelWidth});
        if (pW != newWidth) {
            setPanelWidth(newWidth);
            return 1
        }
        return 0
    }
    const sePanelWidthRightIfDiff = (newWidth: number, pWR: number) => {
        // console.log("‹sePanelWidthRightIfDiff›", {newWidth, panelWidthRight});
        if (pWR != newWidth) {
            setPanelWidthRight(newWidth);
            return 1;
        }
        return 0;
    }


    // horizontal ⇒ mouse down handler for panel resize
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();     // stops text selection
        e.stopPropagation();    // stops bubbling
        setIsResizing(true);
        document.body.style.cursor = 'col-resize';
    };

    // 1️⃣is ipa space 2️⃣ doc type in ipa list
    const isIpa = useMemo(() => {
        return (selectedGroup.assistanttype == assistantCodes.ipa && ipaDocTypes.includes(file?.doc_category || ''));
    }, [selectedGroup, file]);

    const isDpa = useMemo(() => {
        return (selectedGroup.assistanttype == assistantCodes.dpa);
    }, [selectedGroup, file]);

    // 🎯 Alternative citation highlighting via ParagraphOverlay (coexists with regex highlighting).
    // Resolves the clicked value ⇒ paragraph kgnf_ids on the backend (matching over lines_only),
    // then scrolls (loading the page if lazy) and lights the overlay boxes. Falls back to the
    // existing regex highlight (`onDataHighlight`) when the overlay is off or no paragraph matches.
    const onParagraphHighlight = useCallback(async (value: string) => {
        import.meta.env.DEV && console.log("‹overlay_off_or_no_activeFileId›");
        const val = String(value ?? "");
        if (window.paragraphOverlayOn !== true || !activeFileId) {

            onDataHighlight(val);                                   // overlay off ⇒ keep legacy behavior
            return;
        }
        try {
            const res = await qResolveParagraphHighlight(activeFileId, val, selectedGroup.assistanttype, loggedInUser);
            if (res.matched && res.kgnf_ids.length > 0) {
                const ok = await scrollAndHighlightParagraphs(res.kgnf_ids, res.pages);
                if (ok) return;
            }
        } catch {
            import.meta.env.DEV && console.log("‹errr_para_hl_handler›");
        }
        clearCitationHighlights();
        onDataHighlight(val);                                       // graceful fallback ⇒ regex highlight
    }, [activeFileId, selectedGroup.assistanttype, loggedInUser, onDataHighlight]);

    // recalculate panel widths from the divider's actual DOM position (no mouse event needed)
    const syncPanelWidths = useCallback((panelWidthPar: number, panelWidthRightPar: number) => {
        const containerLeft = dividerRef.current?.parentElement?.getBoundingClientRect().left || 0;
        const dividerLeft = dividerRef.current?.getBoundingClientRect().left || 0;
        const newWidth = getNewWidthActual(window.innerWidth, dividerLeft, containerLeft);

        let widthRight = window.innerWidth - (newWidth + resizerBarsWidth);

        if (isDpa ==  true) widthRight = rightDividedRef.current?.getBoundingClientRect().width || 0;

        let spd = sePanelWidthIfDiff(newWidth, panelWidthPar);
        let spr = sePanelWidthRightIfDiff(widthRight, panelWidthRightPar);

    }, []);

    // horizontal panel ⇒ create event handlers for horizontal panel resize
    useEffect(() => {
        // console.log("‹effect_panel_width›", isStructureSideBarOpen);
        if (panelWidth < 600 && isResizing != true){
            setTimeout(() => {
                // console.log("‹sync_widths_on_load›", panelWidth);
                syncPanelWidths(panelWidth, panelWidthRight);
            }, 500);
        }
    }, [showAiOverlay, isStructureSideBarOpen]);

    // horizontal resize ⇒ mouse move handler for panel resize
    const handleMouseMove = (e: MouseEvent) => {
        const containerLeft = dividerRef.current?.parentElement?.getBoundingClientRect().left || 0;
        leftResizing.current = containerLeft;

        eclientx.current = e.clientX;

        const newWidth = getNewWidthActual(window.innerWidth, e.clientX, containerLeft);
        const widthRight = window.innerWidth - (newWidth + resizerBarsWidth);

        setPanelWidth(newWidth);
        setPanelWidthRight(widthRight);
    };

    // horizontal ⇒ mouse up handler
    const handleMouseUp = () => {
        setIsResizing(false);
        document.body.style.cursor = '';
    };

    // vertical ⇒ mouse down handler for panel resize
    const handleVerticalMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();     // stops text selection
        e.stopPropagation();    // stops bubbling
        setIsVerticalResizing(true);
        document.body.style.cursor = 'row-resize';
    };

    // vertical ⇒ mouse move handler for panel resize
    const handleMouseMoveVerticalResize = (e: MouseEvent) => {
        const containerTop = verticalDividerRef.current?.getBoundingClientRect().bottom || 0;
        topResizing.current = Math.min((e.view?.innerHeight || 0) - e.clientY, 600);
        eclienty.current = window.outerHeight - e.clientY;
        const newHeight = topResizing.current;
        setPanelHeight(newHeight);
    };

    // vertical ⇒ mouse up handler
    const handleMouseUpVerticalResize = () => {
        setIsVerticalResizing(false);
        document.body.style.cursor = '';
    };





    // resize trigger handler from pdfbox
    const resizeTriggerBack = (newWidth: number) => {
        if (isResizing) {
            // do nothing - user is resizing
        } else {
            let newWidthActual = getNewWidthActual(window.innerWidth, eclientx.current, leftResizing.current);

            let pwd = sePanelWidthIfDiff(newWidthActual, panelWidth); // setPanelWidth(newWidthActual);
            let pwr = sePanelWidthRightIfDiff((window.innerWidth - (newWidthActual + resizerBarsWidth)), panelWidthRight); // setPanelWidthRight((window.innerWidth - (newWidthActual + resizerBarsWidth)));
            if (pwd || pwr) setResizeBackTriggerCount(prevCount => prevCount + 1);
        }
        setIsResizing(false);
    };

    // horizontal panel ⇒ create event handlers for horizontal panel resize
    useEffect(() => {
        if (isResizing) {
            wasresizingref.current = true;

            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    // vertical panel ⇒ create event handlers for horizontal panel resize
    useEffect(() => {
        if (isVerticalResizing) {
            wasverticalresizingref.current = true;
            window.addEventListener('mousemove', handleMouseMoveVerticalResize);
            window.addEventListener('mouseup', handleMouseUpVerticalResize);
        } else {
            window.removeEventListener('mousemove', handleMouseMoveVerticalResize);
            window.removeEventListener('mouseup', handleMouseUpVerticalResize);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMoveVerticalResize);
            window.removeEventListener('mouseup', handleMouseUpVerticalResize);
        };
    }, [isVerticalResizing]);

    // 1️⃣ has data 2️⃣ doc type not data 3️⃣ not cca, dpa, ipa, vid ¦¦OR¦¦ 1️⃣ is dpa 2️⃣ side bar open
    const shouldBeInWideViewMode = useMemo(() => {
        return (
            (
                file?.invoicedata && file?.invoicedata != '' &&
                file?.doc_category != "data"  &&
                [assistantCodes.cca.toString(), assistantCodes.dpa.toString()].includes(selectedGroup.assistanttype) == false
            ) || (
                selectedGroup.assistanttype == assistantCodes.dpa &&
                isStructureSideBarOpen == true
            )
        );
    }, [selectedGroup, file, isStructureSideBarOpen]);

    // 1️⃣ has data 2️⃣ not cca, ipa, vid
    const hasExtraData = useMemo(() => {
        return (
            file?.invoicedata && file?.invoicedata != '' &&
            [assistantCodes.cca.toString(), assistantCodes.ipa.toString(), assistantCodes.vid.toString()].includes(selectedGroup.assistanttype) == false
        );
    }, [selectedGroup, file, isStructureSideBarOpen]);

    // Show horizontal divider if in invoice space
    // 1️⃣ has data 2️⃣ doc type not data 3️⃣ not cca, dpa
    const notDpaHasData = useMemo(() => {
        return (
            file?.invoicedata && file?.invoicedata != '' &&
            file?.doc_category != "data"  &&
            [assistantCodes.cca.toString(), assistantCodes.dpa.toString()].includes(selectedGroup.assistanttype) == false
        );
    }, [selectedGroup, file]);


    // We should show invoice ui or we are in universal space & dpa sidebar is open
    // 1️⃣ has data 2️⃣ not cca, dpa, vid 3️⃣ doc type not data ¦¦OR¦¦ 1️⃣ is dpa 2️⃣ side bar open
    const hasExtraDataAndDpaSideBarOpen = useMemo(() => {
        return (
            (
                file?.invoicedata && file?.invoicedata != '' &&
                file?.doc_category != "data"  &&
                [assistantCodes.cca.toString(), assistantCodes.dpa.toString(), assistantCodes.vid.toString()].includes(selectedGroup.assistanttype) == false
            ) || (
                selectedGroup.assistanttype == assistantCodes.dpa && isStructureSideBarOpen == true
            )
        );
    }, [selectedGroup, file, isStructureSideBarOpen]);



    // is ipa ¦¦ is dpa with sidebar open
    const shouldResizeHorizontally = useMemo(() => {
        return (
            (selectedGroup.assistanttype == assistantCodes.ipa
                && ipaDocTypes.includes(file?.doc_category || '')) ||
            (selectedGroup.assistanttype == assistantCodes.dpa && isStructureSideBarOpen == true
                && file?.doc_category != "data")
        );
    }, [selectedGroup, file, isStructureSideBarOpen]);


    // 1️⃣is ipa space 2️⃣ has data 3️⃣ doc type in ipa list
    const isIpaWithData = useMemo(() => {
        return (selectedGroup.assistanttype == assistantCodes.ipa &&
            file?.invoicedata && file?.invoicedata != "" &&
            ipaDocTypes.includes(file?.doc_category || ''));
    }, [file]);

    // 1️⃣is invoicesgroup 2️⃣ should be wide view
    const isStructDataAssistantAndShouldBeWideView = useMemo(() => {
        return (selectedGroup.invoicesgroup == 1 && shouldBeInWideViewMode == true);
    }, [selectedGroup, shouldBeInWideViewMode]);

    return (
        <div className={styles.xplrrPDFMain}>
            <div className={styles.xplrrPDFMainInternalWrap} style={{paddingTop: "0"}}>
                <ThemeProvider theme={customTheme}>
                    {/* pdf viewer header */}
                    <div className={isIpa ? styles.pdfMoHeadINV : styles.pdfMoHead}>
                        <div className={styles.headerFileDropdown} >
                            <FileDropdown
                                file={file}
                                fileListStateVar={fileListStateVar}
                                handleFileClick={handleFileClick}
                                onContentShow={onContentShow}
                                activeFileId={activeFileId}
                                isStructureSideBarOpen={isStructureSideBarOpen}
                                setStructureSideBar={(isOpen: boolean) => setIsStructureSideBarOpen(isOpen)}
                                hasExtraData={hasExtraData}
                                groupType={selectedGroup.assistanttype}
                            />
                        </div>

                        {/* pdf viewer header */}
                        {isContentTableShowing && (
                            <Callout target={"#pdfTtldrpTrg"} className={styles.fileListCallout} onDismiss={() => setIsContentTableShowing(false)} directionalHint={DirectionalHint.bottomLeftEdge} isBeakVisible={false}>
                                <div key={contentRefreshTriggerIncrement}>
                                    {renderLinkData({
                                        file,
                                        fileMap,
                                        activeFileId,
                                        currentPage,
                                        referencePage,
                                        styles,
                                        handleClick,
                                        expandCollapseSection,
                                        perFilesPerSectionStateMap
                                    })}
                                </div>
                            </Callout>
                        )}

                        {/* audio controls */}
                        {docAudio && (
                            <div className={styles.volButsContainer}>
                                <div className={styles.volButs}>
                                    {/* speak button */}
                                    <IconButton
                                        onClick={() => (isPlaying ? stopSpeech() : playSpeech())}
                                        disabled={docRendered == true ? false : true}
                                        title="Speak Page"
                                        styles={isPlaying || isLoadingAudio ? pauseButStyles : playButStyles}
                                        className={selectedGroup.enabledocaudio == 1 ? styles.speakButShow : styles.speakButHide}
                                    >
                                        {isPlaying ? (
                                            <HiOutlineVolumeOff size={35} />
                                        ) : isLoadingAudio ? (
                                            <Spinner size={SpinnerSize.small} className={styles.recLoadingSpinner} />
                                        ) : audioRef.current && audioPage == currentPage ? (
                                            <HiMiniPlayPause size={35} />
                                        ) : (
                                            <HiOutlineVolumeUp size={35} />
                                        )}
                                    </IconButton>
                                    {/* close button */}
                                    <IconButton onClick={onXplrrPDFDismiss} title='Close' styles={closeButStyles} className={styles.closeButIC}>
                                        <FaWindowClose size={35} />
                                    </IconButton>
                                </div>
                                <div className={styles.pageSelectorXplrr}>
                                    <div className={styles.pageSelectorOnly}>
                                        <PageSelector
                                            currentPage={currentPage}
                                            numPages={numPages ? numPages : 0}
                                            handlePageChange={handlePageChangeFromSelector}
                                        ></PageSelector>
                                    </div>
                                    <div className={styles.totPagesDisplay}>/ {numPages}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* resizable panels start */}
                    <div className={styles.resizablePannelsStart} ref={leftDividedRef} id="kgnfdivleft">
                        {/* data panels */}
                        {hasExtraDataAndDpaSideBarOpen ? (
                            <div className={styles.dataPanels} style={{ width: panelWidth, transition: isResizing ? "none" : "width 0.2s" }}>
                                {/* inv main fields grid */}
                                {isIpa ? (
                                    <div className={styles.invoiceDataPanel} id={`grdinv_left_tab_${file?.fileid}`} style={{ height: `${window.innerHeight - (panelHeight + 30) }px`}}>
                                        {file && (
                                            <JsonGridInvoice
                                                fileid={activeFileId || ""}
                                                filename={file?.name}
                                                invdata={fileInvData}
                                                loggedInUser={loggedInUser}
                                                onDataHighlight={onDataHighlight}
                                                listRefreshRequest={invRefreshRequestMngCenter}
                                                group={selectedGroup}
                                                sendBackData={(gridData: any) => {setInvUpdate(gridData)}}
                                                transitTotalRowArrMap={transitTotalRowArrMap}
                                                isVersionView={isVersionView}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className={styles.contentDataPanel}>
                                        {/* dpa grid field */}
                                        <div className={styles.analysisTablePanel}>
                                            {/* <JsonGridGen jsonString={file?.invoicedata ?? empty_extraction_field_list} onDataHighlight={onDataHighlight} /> */}
                                            <JsonViewerInteractive
                                                jsonString={file?.invoicedata ?? empty_extraction_field_list}
                                                title=""
                                                sgp={selectedGroup}
                                                onDataHighlight={onDataHighlight}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {/* divider */}
                        {shouldResizeHorizontally ? (
                            <div ref={dividerRef} className={styles.resizableDivider} onMouseDown={handleMouseDown} style={{background: isResizing ? "#ff9d00ff" : "#e0e0e0"}}>
                                <div className={styles.resizableDividerHover}/>
                            </div>
                        ) : (
                            <div ref={dividerRef}/>
                        )}

                        {/* pdf viewer */}
                        <div ref={rightDividedRef} style={{width: shouldResizeHorizontally ? panelWidthRight: "100%", alignItems: "center", justifyContent: "center"}}>
                            <div className={`${isStructDataAssistantAndShouldBeWideView ? styles.pdfViewCA : styles.pdfViewCA}`} style={{width: (isDpa && isStructureSideBarOpen == true ) ? panelWidthRight :"100%"}}>
                                {/* pdf viewer - 1️⃣ invoices 2️⃣ regular */}
                                {isIpa ? (
                                    <XplrrBOXPDFZoom
                                        file={file}
                                        startPage={referencePage}
                                        activeFileId={activeFileId || ''}
                                        handleTimeout={handleTimeout}
                                        handleScroll={handleScroll}
                                        setPages={setPages}
                                        setContentList={setContentList}
                                        callBackVisibleEntries={callBackVisibleEntries}
                                        callBackIsLoading={callBackIsLoading}
                                        srcLoc={srcId}
                                        companyId={selectedGroup.companyid}
                                        panelWidthRight={panelWidthRight}
                                        resizeTriggerBack={resizeTriggerBack}
                                        isResizing={isResizing}
                                    />
                                ) : (
                                    <XplrrBOXPDF
                                        file={file}
                                        startPage={referencePage}
                                        activeFileId={activeFileId || ''}
                                        handleTimeout={handleTimeout}
                                        handleScroll={handleScroll}
                                        setPages={setPages}
                                        setContentList={setContentList}
                                        callBackVisibleEntries={callBackVisibleEntries}
                                        callBackIsLoading={callBackIsLoading}
                                        srcLoc={srcId}
                                        companyId={selectedGroup.companyid}
                                        panelWidthRight={(isDpa && isStructureSideBarOpen == true) ? panelWidthRight : '100%'}
                                        resizeTriggerBack={resizeTriggerBack}
                                        isResizing={isResizing}
                                        shouldBeInWideViewMode={shouldBeInWideViewMode}
                                        parentClassName={`${isStructDataAssistantAndShouldBeWideView ? styles.pdfViewInv : styles.pdfViewCA}`}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {/* resizable panels end */}
                </ThemeProvider>
            </div>

            {/* vertical divider */}
            {isIpaWithData && (
                <div
                    ref={verticalDividerRef}
                    style={{height: "5px", cursor: "row-resize", position: "absolute", bottom: `${panelHeight}px`, background: isVerticalResizing ? "#ff9d00ff" : "#e0e0e0"}}
                    onMouseDown={handleVerticalMouseDown}
                    className={styles.resizeVertical}
                />
            )}

            {/* Invoice Items ￫ Bottom Line-Items Table */}
            {isIpaWithData && (
                <div className={styles.invoiceItemsTableContainer} style={{ height: panelHeight }}>
                    <InvoiceItemsTable
                        jsonString={fileInvData}
                        fid={activeFileId || ''}
                        loggedInUser={loggedInUser}
                        selectedGroup={selectedGroup}
                        listRefreshRequest={invRefreshRequestMngCenter}
                        onDataHighlight={onDataHighlight}
                        onParagraphHighlight={onParagraphHighlight}
                        sendAndSaveInvItems={recieveInvItemsMergeAndSave}
                        invItemsCellUpdCallback={invItemsCellUpdCallback}
                        approved={selectedGroup?.useapproval == 1 ? file?.approved == 1 : undefined}
                    />
                </div>
            )}

            {/* ai extraction overlay */}
            {showAiOverlay && aiOverlayData && (
                <>
                    <div className={styles.aiOverlayToggle} onClick={() => setIsAiOverlayOpen(!isAiOverlayOpen)} style={{ bottom: isAiOverlayOpen ? `${aiOverlayHeight}vh` : '0' }}>
                        <span>{isAiOverlayOpen ? '▼ Hide Post Analysis Results' : '▲ Show Post Analysis Results'}</span>
                    </div>
                    <div className={`${styles.aiOverlayPanel} ${isAiOverlayOpen ? styles.aiOverlayOpen : ''}`} style={isAiOverlayOpen ? { height: `${aiOverlayHeight}vh` } : undefined}>
                        <div className={styles.aiOverlayDragHandle} onMouseDown={handleAiDragStart}>
                            <div className={styles.aiOverlayDragBar} />
                        </div>
                        <div className={styles.aiOverlayContent} style={isAiDragging ? { pointerEvents: 'none' } : undefined} onMouseDown={e => e.stopPropagation()}>
                            <JsonViewerInteractive
                                jsonString={JSON.stringify(aiOverlayData)}
                                title="Post Analysis Results"
                                aiid={file?.aiinteractions?.[0]}
                                fieldorder={aiOverlayFieldorder}
                                sgp={selectedGroup}
                                editable={selectedGroup?.contenttype_config?.allow_human_result_edits == 1}
                                onContentChange={(data, aiid) => {
                                    qUpdateAiContent(aiid, data, loggedInUser).then(ok => {
                                        if (ok) toast.success("Content updated", { autoClose: 1500 });
                                        else toast.error("Failed to update content");
                                    });
                                }}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default XplrrPDF;
